import React from 'react';
import { resolveLogoUrl } from '../../utils/urlUtils';

interface CompanyAvatarProps {
  companyName: string;
  logoUrl?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const CompanyAvatar: React.FC<CompanyAvatarProps> = ({
  companyName,
  logoUrl,
  size = 'md',
  className = ''
}) => {
  // Gradient renkleri - şirket ismine göre tutarlı renk seçimi
  const getGradientColors = (name: string) => {
    const colors = [
      { from: '#3B82F6', to: '#8B5CF6' }, // blue to purple
      { from: '#10B981', to: '#14B8A6' }, // green to teal
      { from: '#F59E0B', to: '#EF4444' }, // orange to red
      { from: '#8B5CF6', to: '#EC4899' }, // purple to pink
      { from: '#6366F1', to: '#3B82F6' }, // indigo to blue
      { from: '#14B8A6', to: '#10B981' }, // teal to green
      { from: '#EC4899', to: '#F43F5E' }, // pink to rose
      { from: '#EAB308', to: '#F59E0B' }, // yellow to orange
      { from: '#06B6D4', to: '#3B82F6' }, // cyan to blue
      { from: '#059669', to: '#10B981' }, // emerald to green
    ];
    
    // Şirket isminin karakterlerinin toplamını kullanarak renk seç
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Size sınıfları
  const sizeClasses = {
    sm: { width: '32px', height: '32px', fontSize: '12px' },
    md: { width: '48px', height: '48px', fontSize: '16px' },
    lg: { width: '64px', height: '64px', fontSize: '18px' },
  };

  // Şirket isminin ilk iki harfini al
  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(companyName);
  const gradientColors = getGradientColors(companyName);
  const sizeStyle = sizeClasses[size];

  const resolvedLogoUrl = logoUrl ? resolveLogoUrl(logoUrl) : null;

  if (resolvedLogoUrl) {
    return (
      <img
        src={resolvedLogoUrl}
        alt={`${companyName} logo`}
        style={{
          width: sizeStyle.width,
          height: sizeStyle.height,
          borderRadius: '12px',
          objectFit: 'cover',
          border: '3px solid rgba(255, 255, 255, 0.3)',
          transition: 'all 0.3s ease',
          position: 'relative',
          zIndex: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        }}
        className={className}
        onError={(e) => {
          // Logo yüklenemezse fallback olarak avatar göster
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const avatarElement = target.nextElementSibling as HTMLElement;
          if (avatarElement) {
            avatarElement.style.display = 'flex';
          }
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: sizeStyle.width,
        height: sizeStyle.height,
        borderRadius: '12px',
        background: `linear-gradient(135deg, ${gradientColors.from} 0%, ${gradientColors.to} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 600,
        fontSize: sizeStyle.fontSize,
        border: '3px solid rgba(255, 255, 255, 0.3)',
        transition: 'all 0.3s ease',
        position: 'relative',
        zIndex: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
      }}
      className={className}
      title={companyName}
    >
      {initials}
    </div>
  );
};

export default CompanyAvatar; 